import { Component, createSignal, Show } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import { useAuth } from '../stores/AuthContext';
import { TbChartBar, TbEye, TbEyeOff } from 'solid-icons/tb';
import Logo from '../components/ui/Logo';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Analytics Dashboard';

const Login: Component = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  const [password, setPassword] = createSignal('');
  const [showPassword, setShowPassword] = createSignal(false);
  const [error, setError] = createSignal('');
  const [loading, setLoading] = createSignal(false);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await auth.login(password());
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please check your password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div class="min-h-screen bg-body dark:bg-dark grid grid-cols-1 lg:grid-cols-[1fr_40%] gap-0">
      {/* Left Side - Login Form */}
      <div class="flex flex-col items-center justify-center py-14">
        <div class="grid w-full max-w-[408px] grid-cols-1 gap-4 px-4">
          {/* Logo and Branding */}
          <div class="mx-auto mb-4 lg:ml-0">
            <Logo size={56} showText={false} />
          </div>

          {/* Heading */}
          <div class="mb-5 text-center lg:text-left">
            <h2 class="mb-2 text-xl font-medium uppercase dark:text-white lg:text-2xl tracking-wider">
              Welcome Back!
            </h2>
            <p class="text-sm text-gray-600 dark:text-gray-300">
              Please login to access your dashboard
            </p>
          </div>

          {/* Form */}
          <form class="grid grid-cols-1 gap-4" onSubmit={handleSubmit}>
            {/* Password Input with Toggle */}
            <div class="relative">
              <label for="password" class="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword() ? 'text' : 'password'}
                required
                class="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-light-dark placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white focus:outline-none focus:border-orange-500 focus:ring-0 transition-colors font-body"
                placeholder="Enter password"
                value={password()}
                onInput={(e) => setPassword(e.currentTarget.value)}
                disabled={loading()}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword())}
                class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                title={showPassword() ? 'Hide password' : 'Show password'}
              >
                {showPassword() ? <TbEye size={20} /> : <TbEyeOff size={20} />}
              </button>
            </div>

            {/* Error Message */}
            <Show when={error()}>
              <div class="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 p-3">
                <p class="text-sm font-medium text-red-800 dark:text-red-200">{error()}</p>
              </div>
            </Show>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading()}
              class="mt-2 w-full py-2.5 px-4 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Show when={loading()} fallback="Sign in">
                <svg
                  class="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Signing in...
              </Show>
            </button>
          </form>
        </div>
      </div>

      {/* Right Side - Gradient Background (Desktop only) */}
      <div class="relative hidden lg:flex bg-gradient-to-br from-orange-400 to-orange-600 dark:from-orange-700 dark:to-orange-900 items-center justify-center">
        <div class="absolute inset-0 opacity-10">
          <svg class="w-full h-full" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="40" fill="white" />
            <circle cx="350" cy="100" r="60" fill="white" />
            <circle cx="100" cy="350" r="50" fill="white" />
            <circle cx="300" cy="300" r="45" fill="white" />
          </svg>
        </div>
        <div class="relative z-10 text-center text-white px-8">
          <TbChartBar size={80} class="mx-auto mb-4" />
          <h3 class="text-3xl font-bold mb-2 uppercase tracking-wider">{APP_NAME}</h3>
          <p class="text-lg opacity-90">Real-time Analytics Dashboard</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
